import { useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { opsMeQuery } from "../api/queries";
import { UserMenu } from "../features/account/UserMenu";
import { LanguageSwitch } from "../features/account/LanguageSwitch";
import { canManageDevelopers } from "../features/ops/accounts/accounts";
import { openDataAlarmsQuery, unknownWarningsQuery } from "../features/ops/dataAlarms/dataAlarms";
import { canMoveData } from "../features/ops/devicedata/devicedata";
import { canEditForecasts } from "../features/ops/forecasts/forecastSettings";
import { canManageMail } from "../features/ops/mail/mailSettings";
import { useOpsRole } from "../features/ops/shared/roles";
import { ErrorMessage } from "../ui/ErrorMessage";
import { serverRoleQuery, useVirtualCreateAllowed } from "../ui/ServerRole";
import { Loading } from "../ui/Spinner";
import { Frame } from "./AppShell";
import { MapStyleProvider } from "../widgets/map/style";

const navLink =
  "block rounded-md px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 [&.active]:bg-brand [&.active]:text-brand-trace";

/** A count next to a menu entry, read out with it (DTC-33, DTC-42); nothing when there is none. */
function Count({ n, label }: { n: number | undefined; label: string }) {
  if (!n) return null;
  return (
    <span className="ml-2 rounded-full bg-red-700 px-1.5 py-0.5 text-xs font-semibold text-white">
      {n > 99 ? "99+" : n}
      <span className="sr-only"> {label}</span>
    </span>
  );
}

function OpsNav() {
  const { t } = useTranslation();
  const { can } = useOpsRole();
  const virtualAllowed = useVirtualCreateAllowed();
  const me = useQuery(opsMeQuery());
  // Device types, data alarms and unknown devices need fleet.read (DTC-61): every console preset but support.
  const catalogue = can("ops-viewer");
  const alarms = useQuery({ ...openDataAlarmsQuery(), enabled: catalogue });
  const unknown = useQuery({ ...unknownWarningsQuery(), enabled: catalogue });
  return (
    <nav aria-label={t("opsNav.label")} className="w-52 shrink-0 border-r border-gray-200 bg-white p-3">
      <ul className="space-y-1">
        <li>
          <Link to="/ops" activeOptions={{ exact: true }} className={navLink}>
            {t("opsNav.home")}
          </Link>
        </li>
        <li>
          <Link to="/ops/fleet" className={navLink}>
            {t("opsNav.fleet")}
          </Link>
        </li>
        {/* The support desk (console-support-desk CSD-01); the API decides which cases each person sees. */}
        <li>
          <Link to="/ops/support" className={navLink}>
            {t("opsNav.support")}
          </Link>
        </li>
        <li>
          <Link to="/ops/fleet-health" className={navLink}>
            {t("opsNav.fleetHealth")}
          </Link>
        </li>
        {/* Addresses and user ids from every source: not for support staff (traffic-log TRF-40). */}
        {can("ops-viewer") && (
          <li>
            <Link to="/ops/traffic" className={navLink}>
              {t("opsNav.traffic")}
            </Link>
          </li>
        )}
        {/* The server's capacity and health (server-health SH-50): every console preset but support. */}
        {can("ops-viewer") && (
          <li>
            <Link to="/ops/server" className={navLink}>
              {t("opsNav.server")}
            </Link>
          </li>
        )}
        {/* Not on a production server (server-role SR-38); the list stays reachable by its address. */}
        {virtualAllowed && (
          <li>
            {can("ops-engineer") ? (
              <Link to="/ops/virtual-devices" className={navLink}>
                {t("opsNav.virtual")}
              </Link>
            ) : (
              // Shown, not hidden, with the role it needs (WEB-F-RND-02, virtual-devices §4).
              <span
                aria-disabled="true"
                className="block cursor-not-allowed rounded-md px-3 py-2 text-sm text-gray-700"
              >
                {t("opsNav.virtual")}
                <span className="block text-xs">{t("console.needsRole", { role: "ops-engineer" })}</span>
              </span>
            )}
          </li>
        )}
        {catalogue && (
          <>
            <li>
              <Link to="/ops/device-types" className={navLink}>
                {t("opsNav.deviceTypes")}
              </Link>
            </li>
            {/* The MQTT data bridge (WEB-BR-01): read with fleet.read, changed with bridge.manage. */}
            <li>
              <Link to="/ops/bridges" className={navLink}>
                {t("opsNav.bridges")}
              </Link>
            </li>
            <li>
              <Link to="/ops/data-alarms" className={navLink}>
                {t("opsNav.dataAlarms")} <Count n={alarms.data} label={t("opsNav.open")} />
              </Link>
            </li>
            <li>
              <Link to="/ops/unknown-devices" className={navLink}>
                {t("opsNav.unknownDevices")} <Count n={unknown.data} label={t("opsNav.last24h")} />
              </Link>
            </li>
            {/* The anonymised weather data store's counts (weather-data-store WDS-04): fleet.read, like the catalogue. */}
            <li>
              <Link to="/ops/weather-data" className={navLink}>
                {t("opsNav.weatherData")}
              </Link>
            </li>
          </>
        )}
        <li>
          <Link to="/ops/firmware" className={navLink}>
            {t("opsNav.firmware")}
          </Link>
        </li>
        <li>
          <Link to="/ops/releases" className={navLink}>
            {t("opsNav.releases")}
          </Link>
        </li>
        <li>
          <Link to="/ops/import" className={navLink}>
            {t("opsNav.import")}
          </Link>
        </li>
        {/* Everyone: their own rows, or every row with log.read (UR-43). */}
        <li>
          <Link to="/ops/actions" className={navLink}>
            {t("opsNav.actions")}
          </Link>
        </li>
        {canMoveData(me.data?.capabilities) && (
          <li>
            <Link to="/ops/device-import" className={navLink}>
              {t("opsNav.deviceImport")}
            </Link>
          </li>
        )}
        <li>
          <Link to="/ops/organisations" className={navLink}>
            {t("opsNav.organisations")}
          </Link>
        </li>
        <li>
          <Link to="/ops/plans" className={navLink}>
            {t("opsNav.plans")}
          </Link>
        </li>
        {/* Hidden without both capabilities (WEB-SRV-DEV-03): it is a platform admin's tool, not a role step. */}
        {canManageDevelopers(me.data?.capabilities) && (
          <li>
            <Link to="/ops/accounts" className={navLink}>
              {t("opsNav.accounts")}
            </Link>
          </li>
        )}
        {/* A platform admin's tool (WEB-STF-01): only for staff.manage. */}
        {me.data?.capabilities?.some((g) => g.capability === "staff.manage") && (
          <li>
            <Link to="/ops/staff" className={navLink}>
              {t("opsNav.staff")}
            </Link>
          </li>
        )}
        {/* The relay every e-mail and both sign-in realms use (WEB-ML-01): staff.manage only, like saving it. */}
        {canManageMail(me.data?.capabilities) && (
          <li>
            <Link to="/ops/settings/mail" className={navLink}>
              {t("opsNav.mail")}
            </Link>
          </li>
        )}
        {/* The forecast provider's calls and settings (WEB-FC-01): a platform admin's switches, like mail. */}
        {canEditForecasts(me.data?.capabilities) && (
          <li>
            <Link to="/ops/settings/forecasts" className={navLink}>
              {t("opsNav.forecasts")}
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}

/** The platform console (`ops` realm, WEB-01). Never linked from the customer navigation. */
export function OpsShell() {
  const { t } = useTranslation();
  const me = useQuery(opsMeQuery());
  // The server's role for the development bar (server-role SR-36); the bar reads it from the cache.
  useQuery(serverRoleQuery("ops"));
  if (me.isPending) return <Loading />;
  if (me.isError) return <ErrorMessage error={me.error} onRetry={() => void me.refetch()} />;
  // The console's maps draw on the server's one open region (map-tiles MAP-10).
  return (
    <MapStyleProvider>
      <Frame
        brand={t("app.console")}
        home="/ops"
        actions={
          <>
            <LanguageSwitch />
            <UserMenu name={me.data.display_name ?? me.data.email} />
          </>
        }
        nav={<OpsNav />}
      >
        <Outlet />
      </Frame>
    </MapStyleProvider>
  );
}
